import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { flatten, head } from "ramda"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { ACCOUNT_SELECTED, AGENDA_MOUNTED, agendaMounted } from "../../../auth/actions"
import { MainMenuAgendaEnum } from "../../../router/types"
import { getHttpHeaders, getSelectedAccountId, getTerminalAuthDataById } from "../../../auth/selectors"
import { createLoadingEnded, createLoadingStarted } from "../../../filters/actions"
import { downloadReportFailure } from "../../reports/actions"
import { fetchDeliverySettings, saveSettingsMutation } from "./request"
import {
    getSelectedServiceDataForDeliverySettings,
    getSelectedServiceIdForDeliverySettings,
    isDeliverySettingsAgendaOpened,
} from "./selectors"
import {
    waitForAccountSelected,
    waitForGlobalFilterSet,
    waitForLocationsLoaded,
} from "../../reports/customers/customersTable/epics"
import {
    DELIVERY_FEE_FORM_SUBMITTED_V2,
    DELIVERY_SETTINGS_SERVICE_SELECTED,
    deliveryFeeFormSubmittedV2Success,
    deliverySettingsServiceSelected,
    fetchDeliverySettingsSuccess,
    UBER_DIRECT_GENERATE_ORGANIZATION_ID_REQUESTED,
    uberDirectGenerateOrganizationIdFailure,
    uberDirectGenerateOrganizationIdSuccess,
} from "./actions"
import type { EventDataResponse, SettingsSaveRequest, Terminal } from "../../../../graphql/types"
import { TerminalType } from "../../../../graphql/types"
import { LOAD_AUTH_LOCATIONS_SUCCESS } from "../../../layouts/header/actions"
import { getCookie, setCookie } from "../../../../utils/cookies"
import { DELIVERY_SETTINGS_PICKER_COOKIE } from "./utils"
import { TerminalAuthData } from "../../../auth/types"
import { createEventRequest, requestEvent } from "../../../../events/utils"
import { EventType } from "../../../../events/types"
import { NotificationType, openNotification } from "../../../uiKit/Notification/Notification"
import { logger } from "../../../../utils/loggerWrap"
import { defer, delayWhen, filter, merge, Observable, of } from "rxjs"
import { ofType, StateObservable } from "redux-observable"
import { Action, StateObject } from "../../../redux/types"
import { catchError, switchMap } from "rxjs/operators"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"

export const deliverySettingsSetServicePicker = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(LOAD_AUTH_LOCATIONS_SUCCESS),
        switchMap((action: Action) => {
            const terminals = flatten(
                action.payload.locations.map(l =>
                    l.terminals.filter(
                        t =>
                            t.type === TerminalType.Register ||
                            t.type === TerminalType.Mpos ||
                            t.type === TerminalType.Mib
                    )
                )
            )
            const cookieServiceId = getCookie(DELIVERY_SETTINGS_PICKER_COOKIE)
            const firstServiceId = head(flatten(terminals.map((t: TerminalAuthData) => t.id)))
            const useFromCookies = cookieServiceId && terminals.some((t: TerminalAuthData) => t.id === cookieServiceId)

            if (useFromCookies) {
                return [deliverySettingsServiceSelected(cookieServiceId)]
            }

            if (firstServiceId) {
                setCookie(DELIVERY_SETTINGS_PICKER_COOKIE, firstServiceId)
            }

            return [deliverySettingsServiceSelected(firstServiceId)]
        })
    )

export const deliverySettingsMountedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGENDA_MOUNTED),
        delayWhen((action: Action) => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen((action: Action) => waitForLocationsLoaded(actions$, deps.getState(), action)),
        filter(action => action.payload.type === MainMenuAgendaEnum.DeliverySettings),
        switchMap(() => {
            const serviceId = getSelectedServiceIdForDeliverySettings(deps.getState())
            if (!serviceId) {
                logger.info("deliverySettingsMountedEpics - no service selected")
                return []
            }

            const service = getTerminalAuthDataById(deps.getState(), serviceId)

            if (!service || !service.locationId) {
                logger.info("deliverySettingsMountedEpics - no service or location id found")
                return []
            }

            return defer(() =>
                fetchDeliverySettings(getHttpHeaders(deps.getState()), service.locationId, serviceId)
            ).pipe(
                switchMap(response => {
                    if (response) {
                        return [
                            fetchDeliverySettingsSuccess(response),
                            createLoadingEnded(MainMenuAgendaEnum.DeliverySettings),
                        ]
                    }

                    return []
                }),
                catchError(error => of(downloadReportFailure(error)))
            )
        })
    )

export const deliveryFeeFormSubmittedV2Epics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELIVERY_FEE_FORM_SUBMITTED_V2),
        switchMap((action: Action) => {
            logger.info("deliveryFeeFormSubmittedV2Epics - form submitted", {
                data: action.payload.data,
            })
            const serviceId = getSelectedServiceIdForDeliverySettings(deps.getState())

            if (!serviceId) {
                return []
            }

            const service = getTerminalAuthDataById(deps.getState(), serviceId)

            if (!service || !service.locationId) {
                return []
            }

            const dataToSave: SettingsSaveRequest = {
                locationId: service.locationId,
                terminalId: serviceId,
                deliverySettings: action.payload.data,
                auditData: constructAuditData(deps, false),
            }
            return defer(() => saveSettingsMutation(getHttpHeaders(deps.getState()), dataToSave)).pipe(
                switchMap(response => {
                    if (response) {
                        return [deliveryFeeFormSubmittedV2Success()]
                    }

                    return []
                }),
                catchError(error => of(downloadReportFailure(error)))
            )
        })
    )

export const serviceChangedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELIVERY_SETTINGS_SERVICE_SELECTED),
        switchMap(() => {
            return [agendaMounted(MainMenuAgendaEnum.DeliverySettings)]
        })
    )

export const startLoadingEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(
            ofType(AGENDA_MOUNTED),
            filter((action: Action) => action.payload.type === MainMenuAgendaEnum.DeliverySettings)
        ),
        actions$.pipe(ofType(ACCOUNT_SELECTED))
    ).pipe(
        switchMap(() => {
            if (isDeliverySettingsAgendaOpened(deps.getState())) {
                return [createLoadingStarted(MainMenuAgendaEnum.DeliverySettings)]
            }
            return []
        })
    )

export const generateOrganizationIdEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(UBER_DIRECT_GENERATE_ORGANIZATION_ID_REQUESTED),
        switchMap(() => {
            const terminal: Maybe<Terminal> = getSelectedServiceDataForDeliverySettings(deps.getState())
            logger.info("generateOrganizationIdEpics - form submitted", {
                terminal: terminal,
            })
            if (!terminal) {
                return []
            }

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.UberDirectOrganizationCreateRequested,
                        {
                            accountId: getSelectedAccountId(deps.getState()) as string,
                            locationId: terminal.locationId,
                            correlationId: deps.idgen.next(),
                        },
                        {
                            locationId: terminal.locationId,
                        }
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.data?.organizationId
                    if (!id) {
                        throw new Error("Id is missing")
                    }

                    return [uberDirectGenerateOrganizationIdSuccess(id)]
                }),
                catchError(error => {
                    openNotification(NotificationType.Error, "Could not generate organization id", `${error}`)
                    return [uberDirectGenerateOrganizationIdFailure(error)]
                })
            )
        })
    )

export default [
    deliverySettingsSetServicePicker,
    deliverySettingsMountedEpics,
    serviceChangedEpics,
    deliveryFeeFormSubmittedV2Epics,
    startLoadingEpics,
    generateOrganizationIdEpics,
]
